import React, { useEffect, useRef, useState } from 'react';
import { ConfigProvider } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTheme } from '../../../context/ThemeContext';
import Table from '../../../commonComponents/Table';
import CustomInput from '../../../commonComponents/input';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useCustomizationSoldOutQuery } from './hooks/useCustomizationSoldOutQuery';
import { useMarkCustomizationAvailable } from './hooks/useCustomizationSoldOutMutations';
import { CustomizationSoldOutItem, CustomizationSoldOutProps } from './types';
import { CUSTOMIZATION_SOLD_OUT_TAB_ID, SEARCH_DEBOUNCE_MS } from './constants';
import {
  DescriptionText,
  NameText,
  PageWrapper,
  SoldOutSwitch,
  SoldOutSwitchWrap,
  TableWrapper,
} from './styles';

const CustomizationSoldOut: React.FC<CustomizationSoldOutProps> = ({ tabIndex }) => {
  const { isDarkMode } = useTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [togglingId, setTogglingId] = useState<number | string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActiveTab = tabIndex === CUSTOMIZATION_SOLD_OUT_TAB_ID;
  const { data, isLoading, isFetching, refetch } = useCustomizationSoldOutQuery(debouncedSearch, isActiveTab);
  const { mutate: markAvailable } = useMarkCustomizationAvailable();

  useEffect(() => {
    if (isActiveTab) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const items: CustomizationSoldOutItem[] = data ?? [];

  const columns: ColumnsType<CustomizationSoldOutItem> = [
    {
      title: 'NAME',
      dataIndex: 'customization',
      key: 'customization',
      render: (name: string) => <NameText>{name}</NameText>,
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'note',
      key: 'note',
      render: (note: string) => <DescriptionText>{note || 'No description available'}</DescriptionText>,
    },
    {
      title: 'SOLD OUT',
      key: 'sold_out',
      align: 'center',
      render: (_, record) => {
        const isThis = togglingId === record.id;
        const isBusy = togglingId !== null && !isThis;
        return (
          <SoldOutSwitchWrap $busy={isBusy}>
            <SoldOutSwitch
              $soldOut={record.sold_out}
              checked={record.sold_out}
              loading={isThis}
              disabled={isBusy}
              onChange={() => {
                setTogglingId(record.id);
                markAvailable(record, { onSettled: () => setTogglingId(null) });
              }}
              checkedChildren="Sold"
              unCheckedChildren="On"
            />
          </SoldOutSwitchWrap>
        );
      },
    },
  ];

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <PageWrapper>
        <CustomInput
          placeholder="Search customizations..."
          value={searchQuery}
          onChange={handleSearch}
          allowClear
          width="300px"
        />

        <TableWrapper>
          <Table<CustomizationSoldOutItem>
            columns={columns}
            dataSource={items}
            rowKey="id"
            loading={isLoading || isFetching}
            pagination={false}
            scroll={items.length > 0 ? { x: true } : undefined}
            disableScroll={items.length === 0}
            emptyText="No sold out customizations found"
            headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
            removeVerticalBorders
            thPadding="15px 10px"
            tdPadding="10px 10px"
            firstChildBorderRadius="8px"
            border="0"
            borderBottomLeftRight
            theme={isDarkMode ? 'dark' : 'light'}
          />
        </TableWrapper>
      </PageWrapper>
    </ConfigProvider>
  );
};

export default CustomizationSoldOut;
